import { type FC, useEffect } from 'react';

import { AccommodationBookingModule } from '@vakantiesnl/components/src/blocks/accommodation';
import { AccommodationProvider } from '@vakantiesnl/services/src/context/useAccommodationContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { usePartyComposition } from '@vakantiesnl/services/src/stores';
import { type Search } from '@vakantiesnl/types';
import dynamic from 'next/dynamic';
import useDeepCompareEffect from 'use-deep-compare-effect';

const AccoOnlyBookingBlock = dynamic(
	import('@vakantiesnl/components/src/blocks/AccoOnlyBookingBlock').then((mod) => mod.AccoOnlyBookingBlock),
	{
		ssr: false,
	},
);

type Props = {
	accommodation: Search.FullAccommodationItem;
	isPriceInView?: boolean;
};

export const AccommodationBlock: FC<Props> = ({ accommodation, isPriceInView }) => {
	const {
		state,
		computed: { durations },
		dispatch: { updateIsInitialLoad },
	} = useTripFilterContext();

	const { departureDate, mealplans, airports, isInitialLoad, withSelectableTransfer } = state;
	const party = usePartyComposition();

	useEffect(() => {
		if (window && window.scrollTo) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, []);

	useDeepCompareEffect(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ accommodationFilterChanges }) =>
				track(
					accommodationFilterChanges(
						party,
						departureDate,
						mealplans,
						durations,
						airports,
						accommodation,
						isInitialLoad,
						withSelectableTransfer,
					),
				),
			),
		);
		/** It is no longer the initial load of the filters */
		if (isInitialLoad) updateIsInitialLoad(false);
	}, [party, departureDate, mealplans, durations, airports, accommodation, withSelectableTransfer]);

	if (accommodation.isBookableQenner) return <AccoOnlyBookingBlock isPriceInView={isPriceInView} />;

	return (
		<AccommodationProvider accommodation={accommodation}>
			<AccommodationBookingModule isPriceInView={isPriceInView} />
		</AccommodationProvider>
	);
};
